import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowLeft, Loader2, ImageIcon, MapPin, Eye, EyeOff, Star, GripVertical,
  ExternalLink, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { ShareButton } from '@/components/ShareButton';
import { shareLinkFor } from '@/lib/share';
import { CopyCaptionButton } from '@/components/admin/CopyCaptionButton';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminPortfolio() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  // Check admin access
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.roles?.includes('admin'))) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch paid orders with portfolio settings
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await authFetch(`${API_BASE}/api/portfolio/admin/orders`);
        if (!response.ok) {
          throw new Error('Failed to load orders');
        }
        const data = await response.json();
        setOrders(data);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && user?.roles?.includes('admin')) {
      fetchOrders();
    }
  }, [isAuthenticated, user]);

  // Toggle portfolio visibility
  const togglePortfolio = async (orderId, currentValue) => {
    setSaving(orderId);
    try {
      const response = await authFetch(`${API_BASE}/api/portfolio/admin/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          show_in_portfolio: !currentValue,
          portfolio_cover_id: null,
          featured: false,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update portfolio settings');
      }

      // Update local state
      setOrders(prev => prev.map(o => 
        o.id === orderId 
          ? { ...o, show_in_portfolio: !currentValue }
          : o
      ));

      toast.success(!currentValue ? 'Added to portfolio' : 'Removed from portfolio');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(null);
    }
  };

  // Toggle featured status
  const toggleFeatured = async (orderId, currentFeatured, showInPortfolio) => {
    if (!showInPortfolio) {
      toast.error('Add to portfolio first before featuring');
      return;
    }

    setSaving(orderId);
    try {
      const response = await authFetch(`${API_BASE}/api/portfolio/admin/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          show_in_portfolio: true,
          portfolio_cover_id: null,
          featured: !currentFeatured,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update featured status');
      }

      setOrders(prev => prev.map(o => 
        o.id === orderId 
          ? { ...o, featured: !currentFeatured }
          : o
      ));

      toast.success(!currentFeatured ? 'Marked as featured' : 'Removed from featured');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
      </div>
    );
  }

  const portfolioOrders = orders.filter(o => o.show_in_portfolio);
  const availableOrders = orders.filter(o => !o.show_in_portfolio);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0a0a0f]/95 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/orders"
                className="p-2 text-[#E8E4DE]/60 hover:text-[#C6A14A] transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-xl font-medium text-white">Portfolio Management</h1>
                <p className="text-sm text-[#E8E4DE]/60">
                  Turn on delivered or paid orders to show them in the public portfolio (watermarked until paid)
                </p>
              </div>
            </div>
            <Link
              to="/portfolio"
              target="_blank"
              className="flex items-center gap-2 text-[#C6A14A] hover:text-[#D4AF5A] text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              <span className="hidden sm:inline">View Public Portfolio</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Currently in Portfolio */}
        <section className="mb-12">
          <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Eye className="h-5 w-5 text-[#C6A14A]" />
            In Portfolio ({portfolioOrders.length})
          </h2>
          
          {portfolioOrders.length === 0 ? (
            <Card className="bg-[#12121a] border-white/10">
              <CardContent className="py-8 text-center">
                <ImageIcon className="h-10 w-10 text-[#E8E4DE]/30 mx-auto mb-3" />
                <p className="text-[#E8E4DE]/60">No properties in portfolio yet</p>
                <p className="text-[#E8E4DE]/40 text-sm mt-1">
                  Toggle properties below to add them
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolioOrders.map((order) => (
                <Card 
                  key={order.id} 
                  className="bg-[#12121a] border-[#C6A14A]/30 overflow-hidden"
                >
                  <div className="relative aspect-video bg-[#1a1a24]">
                    {order.cover_url ? (
                      <img
                        src={order.cover_url}
                        alt={order.property_address}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-10 w-10 text-[#E8E4DE]/20" />
                      </div>
                    )}
                    
                    {/* Featured Badge */}
                    {!order.is_paid && (
                      <Badge className="absolute top-2 left-2 bg-[#050816]/80 text-[#E8E4DE] border border-white/20" data-testid={`portfolio-unpaid-badge-${order.id}`}>
                        Awaiting payment · watermarked
                      </Badge>
                    )}
                    {order.featured && (
                      <Badge className="absolute top-2 right-2 bg-[#C6A14A] text-[#0a0a0f]">
                        <Star className="h-3 w-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                  </div>
                  
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <h3 className="text-white font-medium truncate">
                          {order.city || 'Unknown'}
                        </h3>
                        <p className="text-[#E8E4DE]/60 text-sm truncate">
                          {order.property_address}
                        </p>
                      </div>
                      <Badge variant="outline" className="shrink-0 border-[#C6A14A]/30 text-[#C6A14A]">
                        {order.photo_count} photos
                      </Badge>
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={order.show_in_portfolio}
                          onCheckedChange={() => togglePortfolio(order.id, order.show_in_portfolio)}
                          disabled={saving === order.id}
                          className="data-[state=checked]:bg-[#C6A14A] data-[state=unchecked]:bg-white/15"
                        />
                        <span className="text-sm text-[#E8E4DE]/60">
                          {saving === order.id ? (
                            <Loader2 className="h-3 w-3 animate-spin inline" />
                          ) : (
                            'In Portfolio'
                          )}
                        </span>
                      </div>
                      
                      <button
                        onClick={() => toggleFeatured(order.id, order.featured, order.show_in_portfolio)}
                        disabled={saving === order.id}
                        className={`flex items-center gap-1 text-sm transition-colors ${
                          order.featured 
                            ? 'text-[#C6A14A]' 
                            : 'text-[#E8E4DE]/40 hover:text-[#C6A14A]'
                        }`}
                      >
                        <Star className={`h-4 w-4 ${order.featured ? 'fill-current' : ''}`} />
                        Featured
                      </button>
                    </div>
                    {order.gallery_slug && (
                      <div className="flex items-center gap-2 mt-3">
                        <ShareButton slug={order.gallery_slug} title={order.property_address} label="Copy Link" copyOnly
                          className="flex-1 justify-center" testId={`portfolio-copy-share-${order.id}`} />
                        <CopyCaptionButton order={order} />
                        <a href={shareLinkFor(order.gallery_slug)} target="_blank" rel="noopener noreferrer"
                          className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-white/10 text-[#E8E4DE]/60 hover:text-[#C6A14A] hover:border-[#C6A14A]/40 transition-colors"
                          aria-label="Open share link" data-testid={`portfolio-open-share-${order.id}`}>
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {/* Available Orders */}
        <section>
          <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
            <EyeOff className="h-5 w-5 text-[#E8E4DE]/40" />
            Available Orders ({availableOrders.length})
          </h2>
          
          {availableOrders.length === 0 ? (
            <Card className="bg-[#12121a] border-white/10">
              <CardContent className="py-8 text-center">
                <Check className="h-10 w-10 text-emerald-400/30 mx-auto mb-3" />
                <p className="text-[#E8E4DE]/60">All delivered and paid orders are in the portfolio</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {availableOrders.map((order) => (
                <Card 
                  key={order.id} 
                  className="bg-[#12121a] border-white/10 overflow-hidden hover:border-white/20 transition-colors"
                >
                  <div className="relative aspect-video bg-[#1a1a24]">
                    {order.cover_url ? (
                      <img
                        src={order.cover_url}
                        alt={order.property_address}
                        className="w-full h-full object-cover opacity-60"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-[#E8E4DE]/20" />
                      </div>
                    )}
                  </div>
                  
                  <CardContent className="p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <h3 className="text-white text-sm font-medium truncate">
                          {order.city || 'Unknown'}
                        </h3>
                        <p className="text-[#E8E4DE]/50 text-xs truncate">
                          {order.state}{!order.is_paid && ' · awaiting payment'}
                        </p>
                      </div>
                      <span className="text-[#E8E4DE]/40 text-xs shrink-0">
                        {order.photo_count} photos
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between pt-2 border-t border-white/5">
                      <Switch
                        checked={order.show_in_portfolio}
                        onCheckedChange={() => togglePortfolio(order.id, order.show_in_portfolio)}
                        disabled={saving === order.id}
                      />
                      <span className="text-xs text-[#E8E4DE]/40">
                        {saving === order.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          'Add to Portfolio'
                        )}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
